import React, { type ReactNode, useMemo } from 'react';

import { designSystemTokens } from '../../tokens';

import { TypographyTypes, type weight, Wrapper } from './styles';

export type TypographyProps = {
    className?: string;
    align?: 'inherit' | 'left' | 'center' | 'right' | 'justify';
    background?: 'dark' | 'light';
    children: ReactNode;
    color?: string;
    classes?: string[];
    title?: string;
    gutterBottom?: boolean;
    showEllipsis?: boolean;
    faded?: boolean;
    theme?: 'dark' | 'light';
    variant?: 'div' | 'span' | 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';
    weight?: weight;
    onClick?: () => void;
    applyFontStylingOnInnerEl?: boolean;
} & (
    | {
          type: 'display';
          size?: 50 | 80 | 120 | 160;
      }
    | {
          type: 'headline';
          size?: 18 | 20 | 24 | 30 | 36 | 40 | 50 | 60 | 91;
      }
    | {
          type: 'paragraph';
          size?: 12 | 14 | 15 | 16 | 18 | 20 | 24 | 30;
      }
    | {
          type: 'label';
          size?: 10 | 12 | 14 | 16 | 18 | 20;
      }
);

export const Typography = ({
    className = '',
    align,
    background,
    children,
    classes,
    color,
    gutterBottom,
    faded,
    showEllipsis,
    size,
    theme,
    type,
    variant,
    weight,
    title,
    applyFontStylingOnInnerEl,
    onClick,
}: TypographyProps) => {
    // TODO: Remove background prop once all references are updated to the new theme prop
    if (!theme && background) {
        theme = background;
    }

    const defaultSize = useMemo(() => {
        switch (type) {
            case TypographyTypes.display:
                return 120;
            case TypographyTypes.headline:
                return 20;
            case TypographyTypes.paragraph:
                return 12;
            case TypographyTypes.label:
                return 14;
            default:
                return 12;
        }
    }, [type]);

    const fontColor =
        color || (theme === 'light' ? designSystemTokens.colors.primary.dark : designSystemTokens.colors.primary.light);
    const fontSize = size || defaultSize;
    const textAlign = align || 'inherit';

    return (
        <Wrapper
            title={title}
            as={variant}
            align={textAlign}
            className={`${className || ''} ${classes?.join(' ') || ''}`}
            color={fontColor}
            gutterBottom={!!gutterBottom}
            faded={!!faded}
            showEllipsis={!!showEllipsis}
            size={fontSize}
            type={type}
            weight={weight}
            applyFontStylingOnInnerEl={applyFontStylingOnInnerEl}
            onClick={() => {
                if (onClick) onClick();
            }}
        >
            {children}
        </Wrapper>
    );
};
